import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Eraser, Loader2, Pen, Save, Trash2, TriangleAlert } from "lucide-react";

import {
    BOARD_COLORS,
    BOARD_WIDTHS,
    BoardPoint,
    BoardStroke,
} from "../../domain/whiteboard";
import { WhiteboardChannel } from "../../application/useWhiteboardChannel";
import { drawBoard, drawLastSegment, drawStroke } from "../utils/drawBoard";

type WhiteboardProps = {
    /**
     * The live board. Owned by whatever renders this, so that closing the
     * panel puts the tiles back without throwing the drawing away.
     */
    channel: WhiteboardChannel;
    /** Clearing wipes the other person's work too, so it is the therapist's. */
    canClear?: boolean;
    /** Absent for the patient: only the therapist can put a board on record. */
    onSave?: (imageBase64: string) => Promise<void>;
};

type Size = { width: number; height: number };

function newStrokeId(): string {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
}

/**
 * The shared drawing surface, live for both people in the call.
 *
 * What is drawn here exists only in the two browsers holding the call. It
 * reaches Alchely when — and only when — the therapist saves it.
 */
export default function Whiteboard({ channel, canClear = false, onSave }: WhiteboardProps) {
    const { t } = useTranslation();
    const {
        strokes,
        localIdentity,
        transportFailed,
        publishStrokeStart,
        publishStrokePoints,
        commitLocalStroke,
        clearBoard,
    } = channel;

    const containerRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const liveStroke = useRef<BoardStroke | null>(null);

    const [size, setSize] = useState<Size>({ width: 0, height: 0 });
    const [color, setColor] = useState<string>(BOARD_COLORS[0].value);
    const [width, setWidth] = useState<number>(BOARD_WIDTHS[1].value);
    const [erasing, setErasing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        const element = containerRef.current;
        if (!element) return;

        const measure = () =>
            setSize({ width: element.clientWidth, height: element.clientHeight });
        measure();

        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    // One full repaint whenever the board or its box changes. The stroke
    // currently under the pointer is painted on top, because a stroke
    // arriving from the other side must not wipe the line being drawn here.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || size.width === 0 || size.height === 0) return;

        const ratio = window.devicePixelRatio || 1;
        canvas.width = size.width * ratio;
        canvas.height = size.height * ratio;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        drawBoard(ctx, strokes, size.width, size.height);
        if (liveStroke.current) {
            drawStroke(ctx, liveStroke.current, size.width, size.height);
        }
    }, [strokes, size]);

    const pointAt = useCallback((event: React.PointerEvent<HTMLCanvasElement>): BoardPoint => {
        const rect = event.currentTarget.getBoundingClientRect();
        return {
            x: rect.width === 0 ? 0 : (event.clientX - rect.left) / rect.width,
            y: rect.height === 0 ? 0 : (event.clientY - rect.top) / rect.height,
        };
    }, []);

    const paintLiveSegment = useCallback(() => {
        const canvas = canvasRef.current;
        const stroke = liveStroke.current;
        if (!canvas || !stroke) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        drawLastSegment(ctx, stroke, size.width, size.height);
    }, [size]);

    const onPointerDown = useCallback(
        (event: React.PointerEvent<HTMLCanvasElement>) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            const stroke: BoardStroke = {
                id: newStrokeId(),
                authorId: localIdentity,
                color,
                width,
                erase: erasing,
                points: [pointAt(event)],
            };
            liveStroke.current = stroke;
            setSaved(false);
            publishStrokeStart(stroke);
            paintLiveSegment();
        },
        [color, erasing, localIdentity, paintLiveSegment, pointAt, publishStrokeStart, width]
    );

    const onPointerMove = useCallback(
        (event: React.PointerEvent<HTMLCanvasElement>) => {
            const stroke = liveStroke.current;
            if (!stroke) return;

            const point = pointAt(event);
            stroke.points.push(point);
            publishStrokePoints(stroke.id, point);
            paintLiveSegment();
        },
        [paintLiveSegment, pointAt, publishStrokePoints]
    );

    const onPointerUp = useCallback(() => {
        const stroke = liveStroke.current;
        if (!stroke) return;
        liveStroke.current = null;
        commitLocalStroke(stroke);
    }, [commitLocalStroke]);

    const handleClear = useCallback(() => {
        liveStroke.current = null;
        setSaved(false);
        clearBoard();
    }, [clearBoard]);

    const handleSave = useCallback(async () => {
        if (!onSave) return;
        const canvas = canvasRef.current;
        if (!canvas) return;

        setSaving(true);
        setSaveFailed(false);
        try {
            // Compose onto white in a second canvas: the live board is
            // transparent so the eraser can cut through it, and a transparent
            // PNG in the session record would be unreadable on a dark theme.
            const flat = document.createElement("canvas");
            flat.width = canvas.width;
            flat.height = canvas.height;
            const ctx = flat.getContext("2d");
            if (!ctx) throw new Error("Canvas is unavailable.");
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, flat.width, flat.height);
            ctx.drawImage(canvas, 0, 0);

            const dataUrl = flat.toDataURL("image/png");
            await onSave(dataUrl.slice(dataUrl.indexOf(",") + 1));
            setSaved(true);
        } catch {
            setSaveFailed(true);
        } finally {
            setSaving(false);
        }
    }, [onSave]);

    const toolButton = "flex h-11 min-w-11 items-center justify-center gap-2 rounded-lg border border-transparent px-3 text-sm text-room-ink transition-colors duration-[--duration-fast] hover:bg-room-hover";

    return (
        <div className="flex h-full min-h-0 flex-col gap-2">
            {/* A drawing surface has nothing a screen reader can read out and
                nothing a keyboard can operate, so it is described as a region
                and hidden from the tree rather than announced as an image
                that turns out to be interactive. How full the board is gets
                announced below instead. */}
            <div
                ref={containerRef}
                role="group"
                aria-label={t("whiteboard.canvasLabel")}
                /* Warm paper rather than #ffffff. Pure white measures 18.89:1
                   against the room, which is a torch in a dark room every time
                   the board opens; #faf8f3 still carries every pen colour and
                   is what the saved image looks like too. */
                className="relative min-h-0 flex-1 overflow-hidden rounded-xl border border-room-line bg-room-paper"
            >
                <canvas
                    ref={canvasRef}
                    data-testid="whiteboard-canvas"
                    aria-hidden="true"
                    className="h-full w-full touch-none"
                    style={{ width: "100%", height: "100%" }}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    onPointerLeave={onPointerUp}
                />
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2">
                {/* Every swatch has a border, not just the selected one. The
                    unselected black pen previously had `border-transparent` on
                    a dark bar, so the control a therapist reaches for first had
                    no edge at all — you could only find it by knowing it was
                    there (WCAG 1.4.11). */}
                <div className="flex items-center gap-1 rounded-xl border border-room-line bg-room-raised p-1">
                    {BOARD_COLORS.map((option) => (
                        <button
                            key={option.key}
                            type="button"
                            onClick={() => {
                                setColor(option.value);
                                setErasing(false);
                            }}
                            aria-label={t(`whiteboard.colors.${option.key}`)}
                            aria-pressed={!erasing && color === option.value}
                            className={`flex h-11 w-11 items-center justify-center rounded-lg transition-colors duration-[--duration-fast] ${
                                !erasing && color === option.value
                                    ? "border-2 border-white bg-room-active"
                                    : "border border-room-line hover:bg-room-hover"
                            }`}
                        >
                            <span
                                /* An inset ring, so a dark pen still has an
                                   outline against the swatch it sits on. */
                                className="block h-6 w-6 rounded-full ring-1 ring-inset ring-white/35"
                                style={{ backgroundColor: option.value }}
                            />
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-1 rounded-xl border border-room-line bg-room-raised p-1">
                    {BOARD_WIDTHS.map((option, index) => (
                        <button
                            key={option.key}
                            type="button"
                            onClick={() => setWidth(option.value)}
                            aria-label={t(`whiteboard.widths.${option.key}`)}
                            aria-pressed={width === option.value}
                            className={`${toolButton} ${
                                width === option.value ? "border-white bg-room-active" : ""
                            }`}
                        >
                            <span
                                className="block rounded-full bg-current"
                                style={{
                                    width: `${4 + index * 4}px`,
                                    height: `${4 + index * 4}px`,
                                }}
                            />
                        </button>
                    ))}
                </div>

                <button
                    type="button"
                    onClick={() => setErasing((current) => !current)}
                    aria-pressed={erasing}
                    className={`room-control ${erasing ? "room-control-on" : ""}`}
                >
                    {erasing ? <Eraser size={16} aria-hidden="true" /> : <Pen size={16} aria-hidden="true" />}
                    <span>{erasing ? t("whiteboard.eraser") : t("whiteboard.pen")}</span>
                </button>

                {canClear && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="room-control"
                    >
                        <Trash2 size={16} aria-hidden="true" />
                        <span>{t("whiteboard.clear")}</span>
                    </button>
                )}

                {onSave && (
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="room-control room-control-primary disabled:opacity-50"
                    >
                        {saving ? (
                            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                        ) : (
                            <Save size={16} aria-hidden="true" />
                        )}
                        <span>{t("whiteboard.save")}</span>
                    </button>
                )}
            </div>

            {/* Each state pairs its colour with an icon and a sentence: green
                and amber are the two a therapist is least likely to tell apart,
                and one of them means the board did not save. */}
            <p
                role="status"
                aria-live="polite"
                className="flex min-h-5 items-center justify-center gap-1.5 text-center text-xs"
            >
                {transportFailed && (
                    <span className="flex items-center gap-1.5 text-room-warn">
                        <TriangleAlert size={13} aria-hidden="true" />
                        {t("whiteboard.transportFailed")}
                    </span>
                )}
                {saveFailed && (
                    <span className="flex items-center gap-1.5 text-room-live">
                        <TriangleAlert size={13} aria-hidden="true" />
                        {t("whiteboard.saveFailed")}
                    </span>
                )}
                {saved && !saveFailed && (
                    <span className="flex items-center gap-1.5 text-room-ok">
                        <Check size={13} aria-hidden="true" />
                        {t("whiteboard.saved")}
                    </span>
                )}
                {!transportFailed && !saveFailed && !saved && (
                    <>
                        {/* Said out loud rather than left to be discovered: a
                            board that is never saved leaves no trace at all,
                            and that is a promise worth making visible. */}
                        <span className="text-room-ink-muted">
                            {t("whiteboard.ephemeralNotice")}
                        </span>
                        <span className="sr-only">
                            {t("whiteboard.strokeCount", { count: strokes.length })}
                        </span>
                    </>
                )}
            </p>
        </div>
    );
}
